import {
  initExperimentsClient,
  writeAssignmentsToCookie,
  reconcileWithUser,
  assignmentsEqual,
  experimentsConfig,
} from '../util/experiments';
import { apiBaseUrl } from '../util/api';

// ================ Action types ================ //

const SET_EXPERIMENTS = 'app/experiments/SET_EXPERIMENTS';

// ================ Reducer ================ //

const initialState = {
  assignments: {},
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_EXPERIMENTS:
      return { ...state, assignments: action.payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getExperimentVariant = (state, experimentName) => {
  return state.experiments?.assignments?.[experimentName] || null;
};

export const getExperimentAssignments = state => {
  return state.experiments?.assignments || {};
};

// ================ Action creators ================ //

export const setExperiments = assignments => ({
  type: SET_EXPERIMENTS,
  payload: assignments,
});

// ================ Thunks ================ //

/**
 * Initialize experiments on the client side. If SSR already resolved them
 * (via preloadedState), this just syncs the cookie. Otherwise it resolves fresh.
 */
export const initExperiments = () => (dispatch, getState) => {
  const existing = getState().experiments?.assignments;
  if (existing && Object.keys(existing).length > 0) {
    writeAssignmentsToCookie(existing);
    return;
  }
  const assignments = initExperimentsClient();
  dispatch(setExperiments(assignments));
};

/**
 * Reconcile the cookie/Redux assignments with the user's stored metadata.
 * The user's metadata is the source of truth: valid variants stored on the user override the
 * cookie. New experiments (no user record yet) keep the cookie value and are pushed up.
 * Only POSTs to the sync endpoint when the reconciled view differs from what the user has,
 * so the call is skipped on every login/refresh when there are no changes.
 */
export const syncExperimentsToUser = () => (dispatch, getState) => {
  const state = getState();
  const cookieAssignments = state.experiments?.assignments || {};
  const currentUser = state.user?.currentUser;
  const userId = currentUser?.id?.uuid;

  if (!userId) {
    return Promise.resolve();
  }

  const userAssignments = currentUser?.attributes?.profile?.metadata?.experiments || {};
  const reconciled = reconcileWithUser(experimentsConfig, cookieAssignments, userAssignments);

  if (!assignmentsEqual(cookieAssignments, reconciled)) {
    writeAssignmentsToCookie(reconciled);
    dispatch(setExperiments(reconciled));
  }

  if (assignmentsEqual(userAssignments, reconciled)) {
    return Promise.resolve();
  }

  return fetch(`${apiBaseUrl()}/api/experiments/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ userId, assignments: reconciled }),
  }).catch(() => {
    // Non-critical: don't block auth flow if sync fails
  });
};
